/**
 * Prato do dia no painel (#209): bits de dia da semana (bit 0 = segunda —
 * mesma convenção de PROMOTION_DAYS) e a data local de HOJE no fuso da casa
 * para pré-preencher. Lógica pura (testada em daily-specials.test.ts).
 */
import { PROMOTION_DAYS, intlLocaleFor } from '@self-restaurant/shared'

/**
 * Datas de referência fixas na ordem segunda→domingo: 2024-01-01 é segunda.
 * Meio-dia UTC evita o dia deslocar por fuso. Os bits seguem a mesma
 * convenção de PROMOTION_DAYS (bit 0 = segunda).
 */
const DAY_REFERENCES: ReadonlyArray<readonly [string, number]> = [
  ['2024-01-01', PROMOTION_DAYS.mon],
  ['2024-01-02', PROMOTION_DAYS.tue],
  ['2024-01-03', PROMOTION_DAYS.wed],
  ['2024-01-04', PROMOTION_DAYS.thu],
  ['2024-01-05', PROMOTION_DAYS.fri],
  ['2024-01-06', PROMOTION_DAYS.sat],
  ['2024-01-07', PROMOTION_DAYS.sun],
] as const

export type DayLabel = { bit: number; short: string; long: string }

/**
 * Primeiro dia da semana pelo PAÍS do restaurante (#428, decisão do Eduardo),
 * na convenção do `getDay` do JS (0 = domingo … 6 = sábado): `Intl.Locale`
 * `weekInfo.firstDay` (ISO 1 = segunda … 7 = domingo) do locale composto
 * língua+país (`pt` + `BR` → 'pt-BR' → domingo; `en` + `GB` → segunda;
 * `en` + `US` → domingo). O ICU 77 (CLDR 47) devolve DOMINGO para pt-PT —
 * contra a decisão do produto — por isso Portugal tem override explícito.
 * Sem país, usa a língua (CLDR: `pt` cai na região por omissão, BR →
 * domingo; `en` → US → domingo) e, se o locale for inválido/sem weekInfo,
 * SEGUNDA. Só muda a ORDEM de exibição — o bitmask dos dias fica intacto.
 */
export function weekFirstDay(lang: string, countryCode?: string | null): number {
  const cc = countryCode?.trim().toUpperCase()
  if (cc === 'PT') return 1 // segunda em Portugal — decisão do produto (#428)
  try {
    const locale = new Intl.Locale(intlLocaleFor(lang, cc)) as {
      weekInfo?: { firstDay?: number }
    }
    const firstDay = locale.weekInfo?.firstDay
    if (firstDay && firstDay >= 1 && firstDay <= 7) return firstDay % 7 // ISO → getDay (7=dom → 0)
  } catch {
    /* locale inválido — fallback abaixo */
  }
  return 1 // segunda
}

/**
 * Rótulos dos dias no idioma do painel (#387) — nada de iniciais pt
 * hardcoded: `short` (Intl weekday short) para o botão e `long` para o
 * aria-label/title. A ORDEM começa no primeiro dia da semana do PAÍS do
 * restaurante (#428: pt+PT → segunda, pt+BR/en+US → domingo), sem mexer nos
 * bits.
 */
export function dayLabels(lang: string, countryCode?: string | null): DayLabel[] {
  // locale inválido não pode derrubar o editor: cai no inglês (o fallback do
  // dicionário do painel também é en)
  const formatter = (options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
    try {
      return new Intl.DateTimeFormat(lang, options)
    } catch {
      return new Intl.DateTimeFormat('en', options)
    }
  }
  const labels: DayLabel[] = DAY_REFERENCES.map(([date, bit]) => {
    const instant = new Date(`${date}T12:00:00Z`)
    return {
      bit,
      short: formatter({ weekday: 'short', timeZone: 'UTC' }).format(instant),
      long: formatter({ weekday: 'long', timeZone: 'UTC' }).format(instant),
    }
  })
  // rotação: o 1.º dia da semana do PAÍS passa para a frente (os bits NÃO mudam)
  const offset = (weekFirstDay(lang, countryCode) + 6) % 7 // índice do 1.º dia na base segunda→domingo
  return [...labels.slice(offset), ...labels.slice(0, offset)]
}

/** Bit do dia de semana local (0=domingo…6=sábado) — bit 0 = segunda. */
export function dayBitOf(dayOfWeek: number): number {
  return 1 << ((dayOfWeek + 6) % 7)
}

/** Hoje ('YYYY-MM-DD') no fuso do restaurante — pré-preenchimento do editor. */
export function todayLocal(timezone?: string | null): string {
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone ?? undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const parts: Record<string, string> = {}
    for (const p of fmt.formatToParts(new Date())) parts[p.type] = p.value
    return `${parts.year}-${parts.month}-${parts.day}`
  } catch {
    // fuso inválido: devolve a data local do aparelho (o servidor valida)
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
}

/** A janela gravada inclui HOJE no fuso dado? (espelha client-core no painel) */
export function isSpecialToday(
  special:
    | {
        mode: string
        date: string | null
        days_of_week: number | null
        start_date: string | null
        end_date: string | null
        is_active: boolean
      }
    | null
    | undefined,
  timezone: string | null | undefined,
): boolean {
  if (!special?.is_active) return false
  const today = todayLocal(timezone ?? undefined)
  if (special.mode === 'today') return special.date === today
  if (special.mode === 'range')
    return (
      special.start_date !== null &&
      special.end_date !== null &&
      today >= special.start_date &&
      today <= special.end_date
    )
  if (special.mode === 'weekly' && special.days_of_week !== null) {
    const dow = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone ?? undefined,
      weekday: 'short',
    }).format(new Date())
    const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
    const dayOfWeek = map[dow] ?? 0
    return (special.days_of_week & dayBitOf(dayOfWeek)) !== 0
  }
  return false
}
